import React, { useCallback } from 'react';
import { Trans } from '@lingui/macro';
import ModalContent from 'components/base/modal/modalcontent';
import { Field, Form, Formik, FormikErrors, FormikProps } from 'formik';
import { getExpectedFields } from 'util/backendapi/error';
import { showErrorsInFormik } from 'util/backendapi/error-formik';
import { ReportsAlarmParameter } from 'util/backendapi/types/Model';
import { FormItem, FormSection } from 'components/base/form/FormItem';
import { TransEnum } from 'components/base/i18n/TransEnum';
import { DatetimeField } from 'components/base/form/datefield/datefield';
import { FieldError } from 'components/base/form/errornotice/errornotice';
import ActionBlock from 'components/base/actionblock/actionblock';
import Button, { ButtonPrimary } from 'components/base/button/button';

interface DisableAlarmParameterModalProps {
  alarmParameter: ReportsAlarmParameter;
  timeZone: string;
  onSubmit: (
    reenable_datetime: string | null,
    comment: string
  ) => Promise<void>;
  onCancel: () => void;
}

interface DisableAlarmParameterValues {
  reenable_datetime: string;
  comment: string;
}

export function DisableAlarmParameterModal(
  props: DisableAlarmParameterModalProps
) {
  const { onCancel, alarmParameter, timeZone } = props;

  const validate = useCallback(
    (
      values: DisableAlarmParameterValues
    ): FormikErrors<DisableAlarmParameterValues> => {
      const errors: FormikErrors<DisableAlarmParameterValues> = {};

      if (!values.comment) {
        errors.comment = (<Trans>Please enter a comment.</Trans>) as any;
      }

      return errors;
    },
    []
  );

  return (
    <ModalContent header={<Trans>Temporarily disable alarm parameter</Trans>}>
      <Formik<DisableAlarmParameterValues>
        initialValues={{ reenable_datetime: '', comment: '' }}
        validate={validate}
        onSubmit={async (values, formik) => {
          try {
            const reenable_datetime =
              values.reenable_datetime !== '' ? values.reenable_datetime : null;
            await props.onSubmit(reenable_datetime, values.comment);
          } catch (e) {
            formik.setSubmitting(false);
            showErrorsInFormik(formik, e, getExpectedFields(values));
          }
        }}
      >
        {(formik: FormikProps<DisableAlarmParameterValues>) => (
          <Form>
            {formik.status}
            <FormSection>
              <Trans>
                Temporarily disable the{' '}
                <strong>
                  <TransEnum
                    enum="AlarmParameter_COMPARISON_TYPE"
                    value={alarmParameter.comparison_type}
                  />{' '}
                  <TransEnum
                    enum="AlarmParameter_LEVEL"
                    value={alarmParameter.level}
                  />{' '}
                  <TransEnum
                    enum="AlarmParameter_TYPE"
                    value={alarmParameter.type}
                  />{' '}
                </strong>{' '}
                alarm parameter?
                <p>
                  No alarm reports will be created if readings exceed this alarm
                  parameter while it is disabled.
                </p>
              </Trans>
              <FormItem label={<Trans>Date to re-enable</Trans>}>
                <DatetimeField
                  name="reenable_datetime"
                  timeZone={timeZone}
                  defaultValues={{ hour: '00', minute: '00' }}
                />
                <FieldError name="reenable_datetime" />
              </FormItem>
              <FormItem label={<Trans>Comment</Trans>}>
                <Field component="textarea" name="comment" />
                <FieldError name="comment" />
              </FormItem>
              <p>Leave empty to keep alarm parameter disabled indefinitely.</p>
            </FormSection>
            <ActionBlock>
              <Button onClick={onCancel}>
                <Trans>Cancel</Trans>
              </Button>
              <ButtonPrimary type="submit" disabled={formik.isSubmitting}>
                <Trans>Disable</Trans>
              </ButtonPrimary>
            </ActionBlock>
          </Form>
        )}
      </Formik>
    </ModalContent>
  );
}
